/**
 * /features — canonical features catalog.
 *
 * `/product` is the per-vertical pitch. This page is the deep dive —
 * every capability grouped into five areas with anchored sections that
 * `/vs/*` and blog posts can deep-link to.
 *
 * Audience is the operator evaluating us. Copy stays in business
 * language: no source-code references, no internal type names, no
 * compliance-spec jargon (PCI level codes, SOC numbers, library
 * names). The product owns the technical depth; this page describes
 * the customer benefit.
 *
 * Where a capability differs across verticals (e.g. lien sale vs.
 * eviction vs. impound), use the per-vertical pill block to call out
 * what NestPlot does for each.
 */

import { Link } from "react-router-dom";

import { Btn } from "../../lib/Btn";
import { Card, SectionHeader } from "../../lib/Card";
import { Icon, type IconName } from "../../lib/Icon";
import { Slats } from "../../lib/Slats";
import { useSeo } from "../../lib/useSeo";

type Vertical = "multifamily" | "self_storage" | "parking" | "rv_boat";

const VERTICAL_LABEL: Record<Vertical, string> = {
  multifamily: "Multifamily",
  self_storage: "Self-storage",
  parking: "Parking",
  rv_boat: "RV / boat",
};

interface Capability {
  /** Anchor slug — stable; used by /vs/* and blog deep links. */
  id: string;
  title: string;
  body: string;
  bullets: string[];
  /** Optional per-vertical notes — one short line per applicable vertical. */
  perVertical?: Partial<Record<Vertical, string>>;
  /** Optional "see also" link to a deeper page. */
  see_also?: { label: string; to: string };
}

interface Area {
  id: string;
  title: string;
  blurb: string;
  icon: IconName;
  capabilities: Capability[];
}

const AREAS: Area[] = [
  {
    id: "operator",
    title: "Operator workflow",
    blurb: "The day-to-day surface for operators of every size — one property to a nationwide portfolio.",
    icon: "grid",
    capabilities: [
      {
        id: "portfolio-dashboard",
        title: "Portfolio dashboard",
        body:
          "Cross-property KPIs on one page — occupancy, money collected " +
          "this month, what's overdue, and per-property move-ins / move-outs. " +
          "Scopes to the properties your role allows.",
        bullets: [
          "Period selector: month, quarter, year, trailing 30, trailing 90",
          "Sortable property table with click-through to a single property",
          "Mobile-first — works on your phone in the field",
        ],
      },
      {
        id: "move-in",
        title: "One-step move-in",
        body:
          "When a tenant or resident moves in, everything happens at " +
          "once: their record is created, the unit is marked occupied, " +
          "their access is provisioned, the lease is generated, and the " +
          "welcome email goes out. No half-finished move-ins to clean up.",
        bullets: [
          "Two move-ins can't land on the same unit at the same time",
          "The right lease template is picked for the property's state",
          "Optional ID verification step before the move-in completes",
        ],
        perVertical: {
          multifamily: "Application → lease → resident portal access on day one.",
          self_storage: "Lease → gate code → first month charged in the same flow.",
          parking: "Permit issued → license plate registered → gate or LPR access provisioned.",
          rv_boat: "Slip/bay assigned → vessel or RV record captured → dock or lot access live.",
        },
      },
      {
        id: "tenant-management",
        title: "Tenant / resident management",
        body:
          "Roster, balances, lease info, and a complete timeline per " +
          "person.",
        bullets: [
          "Auto-pay, manual pay, bank (ACH) or card on file",
          "Per-person SMS thread",
          "Import from your existing roster as a CSV; export anytime",
          "Tamper-evident history on every change",
        ],
      },
      {
        id: "today-queue",
        title: "Today queue",
        body:
          "Everything that needs your attention this morning, in one " +
          "list. What lands here depends on what you operate.",
        bullets: [
          "One-click charge retry for failed payments",
          "You confirm before access is turned off — we never auto-revoke a paying tenant",
        ],
        perVertical: {
          multifamily: "Failed rent, leasing-pipeline follow-ups, maintenance tickets, renewal nudges.",
          self_storage: "Failed payments, move-out requests, cancellation refunds, gate revocation queue.",
          parking: "Expired permits, gate-access requests, towing follow-ups, validation codes to issue.",
          rv_boat: "Expired insurance, slip waitlist, haul-out/launch queue, seasonal renewal follow-ups.",
        },
      },
    ],
  },
  {
    id: "tenant",
    title: "Customer experience",
    blurb: "What the tenant, resident, boater, or driver actually sees and does.",
    icon: "users",
    capabilities: [
      {
        id: "public-rental",
        title: "Online rental + application",
        body:
          "Your prospect goes from interested to onboarded entirely from " +
          "their phone, in a few minutes.",
        bullets: [
          "Per-property landing page on your subdomain or custom domain",
          "Sign on a phone — type their name or draw a signature",
          "Secure card capture — card data never touches our servers",
          "24-hour self-serve cancellation with automatic refund",
        ],
        perVertical: {
          multifamily: "Application + screening, hold-deposit, then full lease.",
          self_storage: "Pick a unit, sign, pay, get the gate code on the spot.",
          parking: "Select a permit tier, plate registration, monthly auto-renew.",
          rv_boat: "Slip selection by LOA bracket or RV class, vessel/RV info captured at signing.",
        },
      },
      {
        id: "tenant-portal",
        title: "Customer portal",
        body:
          "Self-serve account view: pay, change card, see the lease, and " +
          "request a move-out — without picking up the phone.",
        bullets: [
          "Magic-link sign-in (no password to remember)",
          "Pay the current balance or any amount by bank (ACH, lowest fees) or card; autopay toggle",
          "Download a fresh signed lease anytime",
          "Initiate a move-out with the notice window you set",
        ],
      },
      {
        id: "identity-verification",
        title: "Identity verification",
        body:
          "Optional per-property gate. The customer submits a government " +
          "ID and a quick selfie before move-in. One verification covers " +
          "every future move-in for the same person across your portfolio.",
        bullets: [
          "$1.50 per verification (passed through to the operator)",
          "Same person, same email — skip the fee on a repeat move-in",
          "Operator can revoke a vouch for fraud cleanup and force a re-verify",
        ],
      },
    ],
  },
  {
    id: "compliance",
    title: "State law + lease compliance",
    blurb: "All-50-state + DC statute coverage — built into the core, not bolted on.",
    icon: "doc",
    capabilities: [
      {
        id: "state-rules",
        title: "Per-state rule engine",
        body:
          "Late-fee caps, grace periods, notice windows, deposit caps, " +
          "and required lease clauses — all indexed by state with the " +
          "governing statute cited.",
        bullets: [
          "Late fees are enforced against the state cap automatically",
          "Reminder cadence matches each state's notice schedule",
          "Compliance deadlines are stamped at notice-send, not at action time",
        ],
        see_also: { label: "Browse state rules", to: "/states" },
      },
      {
        id: "lease-engine",
        title: "Lease engine",
        body:
          "State-specific lease templates with a court-ready audit trail " +
          "on every signature — across each vertical's governing law.",
        bullets: [
          "AI-drafted base, operator-editable",
          "Every signed lease's evidence is captured at signing",
          "Re-render anytime — the original signed lease stays unchanged",
        ],
        perVertical: {
          multifamily: "Landlord-tenant act per state · deposit caps, notice periods, fair-housing disclosures.",
          self_storage: "Self-storage statute per state · late-fee cap, grace period, lien-notice window.",
          parking: "Parking lease law per state · towing rules, validation, and impound notice.",
          rv_boat: "Marina or RV lot agreement · LOA-based pricing, insurance requirements, seasonal terms.",
        },
      },
      {
        id: "delinquency-workflow",
        title: "Delinquency + recovery workflow",
        body:
          "Each vertical has a different end-state for a non-paying " +
          "customer — and a different statute that governs how you get " +
          "there. NestPlot drives the right workflow with the right " +
          "deadlines per state.",
        bullets: [
          "Reminder, late fee, formal notice, and recovery action all per-state",
          "Pause-on-payment: customer catches up, workflow closes automatically",
          "Audit trail on every step (operator and counsel can both pull it)",
        ],
        perVertical: {
          multifamily: "Pay-or-quit notice → eviction filing in the right court, with the right service.",
          self_storage: "Late notice → lien notice → publication → auction or cure, with statutory deadlines.",
          parking: "Notice → tow or boot per state rules → impound release.",
          rv_boat: "Notice → mariner's lien (where applicable) → vessel detention or auction.",
        },
      },
      {
        id: "certified-mail",
        title: "Certified mail delivery",
        body:
          "Where state law requires USPS certified mail with return " +
          "receipt, NestPlot generates the notice and ships it — no " +
          "trip to the post office, no separate billing relationship.",
        bullets: [
          "Address validated before mailing, so notices don't bounce",
          "Tracking number persisted on the related record",
          "Notice PDF stored alongside the tracking — pull both anytime",
        ],
      },
    ],
  },
  {
    id: "financial",
    title: "Financial + accounting",
    blurb: "Payments your way; books reconciled automatically.",
    icon: "wallet",
    capabilities: [
      {
        id: "stripe-connect",
        title: "Operator-owned Stripe Connect",
        body:
          "Tenant rent, resident rent, boater fees, parking permits — " +
          "every dollar lands directly in your Stripe account. NestPlot " +
          "takes 0% of your revenue. Flat monthly fee for the software.",
        bullets: [
          "Operator-owned account; we never see the cash flow",
          "Self-serve refunds within your configured cancellation window",
          "Secure card capture — your customer's card data never touches us",
        ],
      },
      {
        id: "multi-entity-accounting",
        title: "Multi-entity accounting",
        body:
          "QuickBooks Online, Xero, and Sage Intacct two-way sync — per " +
          "LLC, with separate books per entity.",
        bullets: [
          "Each LLC syncs to its own book on its own connection",
          "Per-property dimension mapped to QBO Class or Xero Tracking",
          "Sync retries can never duplicate an invoice",
        ],
      },
      {
        id: "pricing-ai",
        title: "Pricing AI (advisory)",
        body:
          "Weekly per-unit recommendations with a plain-English rationale. " +
          "Auto-apply policy is operator-set (off / small changes only / " +
          "all) with a cap on how much a rate can move at once.",
        bullets: [
          "Advisory by default — we never change rates without your nod",
          "Market-comp aware at the local level",
          "Every applied change is recorded in your rate history",
        ],
        perVertical: {
          multifamily: "Per-unit-mix renewal recs based on local comps and lease tenure.",
          self_storage: "Per-unit-size recs with seasonality and ZIP-level comp data.",
          parking: "Permit and transient rate recs by lot and time-of-day.",
          rv_boat: "Slip and bay recs by LOA bracket or RV class and season.",
        },
      },
    ],
  },
  {
    id: "platform",
    title: "Platform — security, team, access",
    blurb: "The underpinnings: roles, audit trail, and gate / access vendors.",
    icon: "lock",
    capabilities: [
      {
        id: "rbac",
        title: "Team permissions",
        body:
          "Role defaults plus per-person overrides. Permissions that " +
          "involve a money limit (like refund caps) support an amount " +
          "check.",
        bullets: [
          "Roles: owner, admin, manager, viewer",
          "Per-property scopes — restrict managers to specific sites",
          "Grant one extra permission to a manager without making them an admin",
          "If a teammate can't do something, the action is hidden from them",
        ],
      },
      {
        id: "audit-log",
        title: "Tamper-evident audit trail",
        body:
          "Every change is recorded with who did it, when, from where, " +
          "and what the before / after looked like.",
        bullets: [
          "Filterable by action, property, person, or target",
          "Available on every detail view, not just at the system level",
          "Sufficient for SOC 2 audits — already in place",
        ],
      },
      {
        id: "access-vendors",
        title: "Access hardware integrations",
        body:
          "Per-property credentials, fail-safe provisioning — paying " +
          "tenants always get in — and a 'Test connection' step so you " +
          "catch a misconfiguration at setup, not at move-in day.",
        bullets: [
          "You confirm before access is turned off — we never auto-revoke a paying tenant",
          "Sandbox for every vendor — verify the integration before move-in day",
        ],
        perVertical: {
          multifamily: "Mobile-key + fob building access integrations across major vendors.",
          self_storage: "PTI Falcon, SecurGuard, INSOMNIAC CIA, Noke, DaVinciLock, OpenTech IntelliGate, Janus.",
          parking: "LPR (license plate recognition) at gate, QR-code validation for visitors.",
          rv_boat: "Gated dock and lot access; dry-stack lift queue with operator confirmation.",
        },
      },
      {
        id: "team",
        title: "Team seats + invitations",
        body:
          "Invite teammates by email with a role and a per-property " +
          "scope. They accept via magic link — no shared passwords.",
        bullets: [
          "Invites expire in 14 days; revoke at any time",
          "Same person can be invited to a second organization",
          "Only people with the right permission can invite or remove",
        ],
      },
      {
        id: "branding",
        title: "Per-property branding",
        body:
          "Logo, accent color, and custom domain. HTTPS managed for you " +
          "— no DNS gymnastics on your side.",
        bullets: [
          "Customer portal renders in your colors",
          "Custom domain: add a CNAME, we mint and renew the certificate",
          "Your logo on every customer-facing surface",
        ],
      },
    ],
  },
];

export function FeaturesPage() {
  useSeo({
    title: "NestPlot features — every capability, organized",
    description:
      "The full NestPlot capability catalog across multifamily, self-storage, parking, and RV/boat: portfolio dashboard, vendor gate integration, leasing pipeline, maintenance ticketing, multi-entity accounting, identity verification, state-compliant lease and recovery workflows, team permissions, audit trail, and more.",
    path: "/features",
  });

  return (
    <div className="mx-auto max-w-[1080px] px-5 lg:px-8 py-10 lg:py-16 space-y-12">
      <div>
        <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
          Features
        </div>
        <h1 className="font-display font-black text-[42px] lg:text-[64px] leading-[0.96] tracking-[-0.04em] mt-2">
          Everything <span className="text-accent">NestPlot</span> does
        </h1>
        <p className="text-sub text-[17px] mt-4 max-w-[680px]">
          The full capability catalog — every piece running in production
          across multifamily, self-storage, parking, and RV/boat. Operator
          workflow, customer experience, state-law compliance, financial
          integrations, platform security. Pick a card to drill in.
        </p>
        <div className="mt-6 flex gap-3 flex-wrap">
          <Link to="/signup">
            <Btn kind="primary" size="lg" iconRight={<Icon name="arrow-right" size={16} />}>
              Start free trial
            </Btn>
          </Link>
          <Link to="/pricing">
            <Btn kind="ghost" size="lg">
              See pricing
            </Btn>
          </Link>
          <Link to="/vs">
            <Btn kind="ghost" size="lg">
              Compare vs others
            </Btn>
          </Link>
        </div>
      </div>

      <Card>
        <SectionHeader title="Jump to" sub="Five areas, every capability shipped." />
        <div className="px-5 py-5 grid sm:grid-cols-2 md:grid-cols-3 gap-3 text-[13.5px]">
          {AREAS.map((a) => (
            <a
              key={a.id}
              href={`#${a.id}`}
              className="flex items-center gap-2 font-semibold hover:text-accent"
            >
              <Icon name={a.icon} size={14} className="text-accent" />
              {a.title}
              <span className="text-sub text-[11px] ml-auto font-mono">
                {a.capabilities.length}
              </span>
            </a>
          ))}
        </div>
      </Card>

      {AREAS.map((area) => (
        <section key={area.id} id={area.id} className="scroll-mt-20">
          <div className="flex items-start gap-3 mb-6">
            <div className="w-11 h-11 rounded-md bg-accent-soft border border-line grid place-items-center text-accent shrink-0">
              <Icon name={area.icon} size={20} />
            </div>
            <div>
              <h2 className="font-display font-black text-[28px] lg:text-[34px] tracking-[-0.02em] uppercase leading-tight">
                {area.title}
              </h2>
              <p className="text-sub text-[15px] mt-1 max-w-[600px]">{area.blurb}</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {area.capabilities.map((c) => (
              <Card key={c.id}>
                <div className="bg-accent-soft px-5 pt-3 pb-2">
                  <Slats color="var(--accent)" opacity={0.7} height={10} />
                </div>
                <div id={c.id} className="px-6 py-6 scroll-mt-20">
                  <h3 className="font-display font-extrabold text-[18px] uppercase tracking-[-0.01em] mb-2">
                    {c.title}
                  </h3>
                  <p className="text-sub text-[13.5px] mt-1">{c.body}</p>
                  <ul className="mt-4 space-y-2 text-[13px]">
                    {c.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2">
                        <Icon name="check" size={13} className="text-ok mt-[3px] shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  {c.perVertical && (
                    <div className="mt-5 border-t border-line pt-4">
                      <div className="font-mono text-[10.5px] text-sub tracking-[0.14em] uppercase mb-2">
                        How it shows up per vertical
                      </div>
                      <ul className="space-y-1.5 text-[12.5px]">
                        {(Object.keys(c.perVertical) as Vertical[]).map((v) => (
                          <li key={v} className="flex items-start gap-2">
                            <span className="font-semibold text-ink shrink-0 w-[100px]">
                              {VERTICAL_LABEL[v]}
                            </span>
                            <span className="text-sub">{c.perVertical![v]}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {c.see_also && (
                    <Link
                      to={c.see_also.to}
                      className="mt-4 inline-block text-accent text-[12px] font-mono uppercase tracking-[0.1em] hover:underline"
                    >
                      {c.see_also.label} →
                    </Link>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </section>
      ))}

      <Card>
        <div className="px-8 py-10 text-center space-y-4">
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
            Try it free for 30 days
          </div>
          <h2 className="font-display font-black text-[28px] lg:text-[36px] tracking-[-0.025em]">
            See it running on your property.
          </h2>
          <p className="text-sub max-w-[480px] mx-auto text-[14px]">
            No card. No sales call. Migration help for the first 100 customers.
            Payments, access, and state-specific leases work from day one.
          </p>
          <div className="flex justify-center gap-3 pt-2">
            <Link to="/signup">
              <Btn kind="primary" size="md" iconRight={<Icon name="arrow-right" size={14} />}>
                Start free trial
              </Btn>
            </Link>
            <Link to="/contact">
              <Btn kind="secondary" size="md">
                Contact sales
              </Btn>
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
}
